import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import {
  FiMail,
  FiSearch,
  FiX,
  FiCalendar,
  FiEdit2,
  FiTrash2,
  FiUsers,
  FiCheck,
  FiBell,
} from "react-icons/fi";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { CardSkeleton } from "@/components/common";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";

type Subscriber = {
  id: string;
  email: string;
  name: string | null;
  status: string;
  source: string | null;
  subscribedAt: string;
  unsubscribedAt: string | null;
  prefsNewsletter: boolean;
  prefsRateAlerts: boolean;
  prefsMarketUpdates: boolean;
  prefsPromotions: boolean;
  customerAccountStatus: string | null;
};

const statusConfig: Record<string, { label: string; color: string }> = {
  active: { label: "Active", color: "bg-green-100 text-green-700" },
  unsubscribed: { label: "Unsubscribed", color: "bg-gray-100 text-gray-700" },
  bounced: { label: "Bounced", color: "bg-red-100 text-red-700" },
};

const accountStatusConfig: Record<string, { label: string; color: string }> = {
  existing: { label: "Existing Customer", color: "bg-emerald-100 text-emerald-700" },
  ineligible: { label: "Ineligible", color: "bg-amber-100 text-amber-700" },
  discharged: { label: "Discharged", color: "bg-slate-100 text-slate-600" },
};

const prefLabels: { key: keyof Pick<Subscriber, "prefsNewsletter" | "prefsRateAlerts" | "prefsMarketUpdates" | "prefsPromotions">; label: string }[] = [
  { key: "prefsNewsletter", label: "Newsletter" },
  { key: "prefsRateAlerts", label: "Rate Alerts" },
  { key: "prefsMarketUpdates", label: "Market Updates" },
  { key: "prefsPromotions", label: "Promotions" },
];

export function SubscribersPage() {
  const [subscribers, setSubscribers] = useState<Subscriber[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("all");

  // Edit state
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editSubscriber, setEditSubscriber] = useState<Subscriber | null>(null);
  const [editForm, setEditForm] = useState({
    name: "",
    email: "",
    status: "active",
    prefsNewsletter: true,
    prefsRateAlerts: true,
    prefsMarketUpdates: true,
    prefsPromotions: false,
  });
  const [isSaving, setIsSaving] = useState(false);

  // Delete state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteSubscriber, setDeleteSubscriber] = useState<Subscriber | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const fetchSubscribers = useCallback(async () => {
    try {
      const response = await fetch("/api/admin/subscribers", {
        credentials: "include",
      });
      if (response.ok) {
        const data = (await response.json()) as Subscriber[];
        setSubscribers(data);
      }
    } catch (error) {
      console.error("Failed to fetch subscribers:", error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSubscribers();
  }, [fetchSubscribers]);

  // Open edit dialog
  const openEdit = (sub: Subscriber) => {
    setEditSubscriber(sub);
    setEditForm({
      name: sub.name || "",
      email: sub.email,
      status: sub.status,
      prefsNewsletter: sub.prefsNewsletter ?? true,
      prefsRateAlerts: sub.prefsRateAlerts ?? true,
      prefsMarketUpdates: sub.prefsMarketUpdates ?? true,
      prefsPromotions: sub.prefsPromotions ?? false,
    });
    setEditDialogOpen(true);
  };

  // Save edit
  const handleSave = async () => {
    if (!editSubscriber) return;
    setIsSaving(true);
    try {
      const response = await fetch(`/api/admin/subscribers/${editSubscriber.id}`, {
        method: "PATCH",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(editForm),
      });
      if (response.ok) {
        toast.success("Subscriber updated");
        setEditDialogOpen(false);
        setEditSubscriber(null);
        fetchSubscribers();
      } else {
        const data = (await response.json()) as { error?: string };
        toast.error(data.error || "Failed to update subscriber");
      }
    } catch {
      toast.error("Failed to update subscriber");
    } finally {
      setIsSaving(false);
    }
  };

  // Toggle a single preference inline (quick toggle)
  const togglePref = async (sub: Subscriber, key: keyof Pick<Subscriber, "prefsNewsletter" | "prefsRateAlerts" | "prefsMarketUpdates" | "prefsPromotions">) => {
    try {
      const response = await fetch(`/api/admin/subscribers/${sub.id}`, {
        method: "PATCH",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ [key]: !sub[key] }),
      });
      if (response.ok) {
        // Update locally for instant feedback
        setSubscribers((prev) =>
          prev.map((s) => (s.id === sub.id ? { ...s, [key]: !s[key] } : s))
        );
      } else {
        toast.error("Failed to update preference");
      }
    } catch {
      toast.error("Failed to update preference");
    }
  };

  // Delete
  const handleDelete = async () => {
    if (!deleteSubscriber) return;
    setIsDeleting(true);
    try {
      const response = await fetch(`/api/admin/subscribers/${deleteSubscriber.id}`, {
        method: "DELETE",
        credentials: "include",
      });
      if (response.ok) {
        toast.success("Subscriber removed");
        setDeleteDialogOpen(false);
        setDeleteSubscriber(null);
        fetchSubscribers();
      } else {
        const data = (await response.json()) as { error?: string };
        toast.error(data.error || "Failed to delete subscriber");
      }
    } catch {
      toast.error("Failed to delete subscriber");
    } finally {
      setIsDeleting(false);
    }
  };

  // Filter
  const filteredSubscribers = subscribers.filter((sub) => {
    const matchesSearch =
      sub.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
      sub.name?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = statusFilter === "all" || sub.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const activeCount = subscribers.filter((s) => s.status === "active").length;
  const unsubCount = subscribers.filter((s) => s.status === "unsubscribed").length;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="font-serif text-3xl font-medium text-slate-900">
          Subscribers
        </h1>
        <p className="text-muted-foreground mt-1">
          Manage newsletter subscribers and their preferences
        </p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-4 pb-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-primary/10 rounded-lg">
                <FiUsers className="w-5 h-5 text-primary" />
              </div>
              <div>
                <p className="text-2xl font-bold">{subscribers.length}</p>
                <p className="text-xs text-muted-foreground">Total</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 pb-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <FiCheck className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{activeCount}</p>
                <p className="text-xs text-muted-foreground">Active</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 pb-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-gray-100 rounded-lg">
                <FiX className="w-5 h-5 text-gray-500" />
              </div>
              <div>
                <p className="text-2xl font-bold">{unsubCount}</p>
                <p className="text-xs text-muted-foreground">Unsubscribed</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 pb-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <FiBell className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">
                  {subscribers.filter((s) => s.prefsNewsletter && s.status === "active").length}
                </p>
                <p className="text-xs text-muted-foreground">Newsletter</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground w-4 h-4" />
          <Input
            placeholder="Search subscribers..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
          />
          {searchQuery && (
            <button
              onClick={() => setSearchQuery("")}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
            >
              <FiX className="w-4 h-4" />
            </button>
          )}
        </div>
        <div className="flex gap-2">
          {["all", "active", "unsubscribed", "bounced"].map((status) => (
            <Button
              key={status}
              variant={statusFilter === status ? "default" : "outline"}
              size="sm"
              onClick={() => setStatusFilter(status)}
              className="capitalize"
            >
              {status}
            </Button>
          ))}
        </div>
      </div>

      {/* Subscribers List */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FiMail className="w-5 h-5" />
            All Subscribers ({filteredSubscribers.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="space-y-3">
              <CardSkeleton />
              <CardSkeleton />
              <CardSkeleton />
            </div>
          ) : filteredSubscribers.length === 0 ? (
            <div className="text-center py-8">
              <FiMail className="w-12 h-12 mx-auto text-muted-foreground/50 mb-4" />
              <p className="text-muted-foreground">
                {searchQuery || statusFilter !== "all"
                  ? "No subscribers found"
                  : "No subscribers yet"}
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {filteredSubscribers.map((subscriber) => (
                <div
                  key={subscriber.id}
                  className="flex flex-col lg:flex-row lg:items-center gap-4 p-4 rounded-lg bg-muted/50 hover:bg-muted/70 transition-colors"
                >
                  {/* Info */}
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="font-medium">
                        {subscriber.name || subscriber.email}
                      </p>
                      <Badge
                        variant="secondary"
                        className={statusConfig[subscriber.status]?.color || ""}
                      >
                        {statusConfig[subscriber.status]?.label || subscriber.status}
                      </Badge>
                      {subscriber.customerAccountStatus ? (
                        <Badge
                          variant="secondary"
                          className={accountStatusConfig[subscriber.customerAccountStatus]?.color || "bg-blue-100 text-blue-700"}
                        >
                          {accountStatusConfig[subscriber.customerAccountStatus]?.label || subscriber.customerAccountStatus}
                        </Badge>
                      ) : (
                        <Badge variant="outline" className="text-muted-foreground border-dashed">
                          Not a Customer
                        </Badge>
                      )}
                    </div>
                    {subscriber.name && (
                      <p className="text-sm text-muted-foreground">
                        {subscriber.email}
                      </p>
                    )}
                    <div className="flex items-center gap-4 mt-1 text-xs text-muted-foreground">
                      <span className="flex items-center gap-1">
                        <FiCalendar className="w-3 h-3" />
                        {new Date(subscriber.subscribedAt).toLocaleDateString()}
                      </span>
                      {subscriber.source && (
                        <span className="capitalize">
                          via {subscriber.source.replace("_", " ")}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Preference Badges - clickable toggles */}
                  <div className="flex items-center gap-1.5 flex-wrap">
                    {prefLabels.map(({ key, label }) => (
                      <button
                        key={key}
                        onClick={() => togglePref(subscriber, key)}
                        className={`px-2.5 py-1 rounded-full text-xs font-medium transition-all cursor-pointer ${
                          subscriber[key]
                            ? "bg-primary/10 text-primary border border-primary/20"
                            : "bg-muted text-muted-foreground border border-transparent hover:border-slate-300"
                        }`}
                        title={`${subscriber[key] ? "Disable" : "Enable"} ${label}`}
                      >
                        {label}
                      </button>
                    ))}
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-2 shrink-0">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => openEdit(subscriber)}
                      title="Edit"
                    >
                      <FiEdit2 className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        setDeleteSubscriber(subscriber);
                        setDeleteDialogOpen(true);
                      }}
                      className="text-red-600 hover:text-red-700 hover:bg-red-50"
                      title="Delete"
                    >
                      <FiTrash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Edit Subscriber</DialogTitle>
            <DialogDescription>
              Update subscriber details and email preferences
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium">Name</label>
                <Input
                  value={editForm.name}
                  onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                  placeholder="Subscriber name"
                />
              </div>
              <div>
                <label className="text-sm font-medium">Email</label>
                <Input
                  type="email"
                  value={editForm.email}
                  onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                />
              </div>
            </div>

            <div>
              <label className="text-sm font-medium">Status</label>
              <div className="flex gap-2 mt-1">
                {["active", "unsubscribed"].map((s) => (
                  <Button
                    key={s}
                    type="button"
                    variant={editForm.status === s ? "default" : "outline"}
                    size="sm"
                    onClick={() => setEditForm({ ...editForm, status: s })}
                    className="capitalize"
                  >
                    {s}
                  </Button>
                ))}
              </div>
            </div>

            <div>
              <label className="text-sm font-medium mb-2 block">Email Preferences</label>
              <div className="space-y-2">
                {prefLabels.map(({ key, label }) => (
                  <label
                    key={key}
                    className="flex items-center justify-between p-3 rounded-lg border hover:bg-muted/50 cursor-pointer"
                  >
                    <span className="text-sm">{label}</span>
                    <input
                      type="checkbox"
                      checked={editForm[key]}
                      onChange={(e) => setEditForm({ ...editForm, [key]: e.target.checked })}
                      className="h-4 w-4 rounded"
                    />
                  </label>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialogOpen(false)} disabled={isSaving}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={isSaving}>
              {isSaving ? "Saving..." : "Save Changes"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="text-red-600 flex items-center gap-2">
              <FiTrash2 className="w-5 h-5" />
              Remove Subscriber
            </DialogTitle>
            <DialogDescription>
              This will permanently remove this subscriber from your list.
            </DialogDescription>
          </DialogHeader>
          {deleteSubscriber && (
            <div className="p-4 bg-red-50 rounded-lg border border-red-200">
              <p className="font-medium">{deleteSubscriber.name || deleteSubscriber.email}</p>
              {deleteSubscriber.name && (
                <p className="text-sm text-muted-foreground">{deleteSubscriber.email}</p>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)} disabled={isDeleting}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDelete} disabled={isDeleting}>
              {isDeleting ? "Removing..." : "Remove Subscriber"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
